import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient, { resolveBaseURL } from "../../../utils/apiClient";
import { hasPermission, PERMISSIONS } from "../../../utils/permissions";
import Loader from "../../Loader";

const STATUS_OPTIONS = ["Pending", "Approved", "Rejected", "All"];

const ListSchoolPayments = () => {
  const { user } = useSelector((state) => state.auth || {});
  const canReviewPayments = hasPermission(user, PERMISSIONS.SCHOOLS_PAYMENTS_REVIEW);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [actionId, setActionId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingPayment, setRejectingPayment] = useState(null);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = statusFilter === "All" ? {} : { status: statusFilter };
      const { data } = await apiClient.get("/admin/school-payments", { params });
      setPayments(Array.isArray(data?.payments) ? data.payments : []);
    } catch (err) {
      const message =
        err?.response?.data?.errMessage ||
        err?.response?.data?.message ||
        "Failed to load school payments.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const pendingCount = useMemo(
    () => payments.filter((payment) => payment.status === "Pending").length,
    [payments]
  );

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const formatAmount = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "-";
    return `₦${amount.toLocaleString()}`;
  };

  const handleApprove = async (paymentId) => {
    if (!window.confirm("Approve this receipt and mark the batch students as paid?")) {
      return;
    }

    setActionId(paymentId);
    setError("");

    try {
      await apiClient.post(`/admin/school-payments/${paymentId}/approve`);
      await loadPayments();
    } catch (err) {
      const message =
        err?.response?.data?.errMessage ||
        err?.response?.data?.message ||
        "Failed to approve payment.";
      setError(message);
    } finally {
      setActionId("");
    }
  };

  const handleReject = async () => {
    if (!rejectingPayment) return;

    setActionId(rejectingPayment.id);
    setError("");

    try {
      await apiClient.post(`/admin/school-payments/${rejectingPayment.id}/reject`, {
        reason: rejectReason,
      });
      setRejectingPayment(null);
      setRejectReason("");
      await loadPayments();
    } catch (err) {
      const message =
        err?.response?.data?.errMessage ||
        err?.response?.data?.message ||
        "Failed to reject payment.";
      setError(message);
    } finally {
      setActionId("");
    }
  };

  const openReceipt = (paymentId) => {
    const baseUrl = resolveBaseURL();
    window.open(`${baseUrl}/admin/school-payments/${paymentId}/receipt`, "_blank");
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            School Portal
            <small>Payment Receipts</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li>
              <Link to="/admin/schools">School Portal</Link>
            </li>
            <li className="active">Payment Receipts</li>
          </ol>
        </section>

        <section className="content">
          <div className="box box-primary">
            <div className="box-header with-border">
              <h3 className="box-title">Receipt Review Queue</h3>
              <div className="box-tools pull-right">
                <select
                  className="form-control input-sm"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  style={{ width: 160 }}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="box-body">
              {error && <div className="alert alert-danger">{error}</div>}

              {statusFilter === "Pending" && !loading && (
                <p className="text-muted">
                  {pendingCount} receipt{pendingCount === 1 ? "" : "s"} awaiting review.
                </p>
              )}

              {loading ? (
                <Loader />
              ) : payments.length === 0 ? (
                <div className="alert alert-info">No payments found for this filter.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Submitted</th>
                        <th>School</th>
                        <th>Batch</th>
                        <th>Students</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Receipt</th>
                        <th style={{ minWidth: 180 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.initiatedDate || payment.date)}</td>
                          <td>
                            <strong>{payment.schoolName || "-"}</strong>
                            <br />
                            <small>{payment.schoolEmail || "-"}</small>
                          </td>
                          <td>{payment.batchName || "-"}</td>
                          <td>{payment.studentCount ?? "-"}</td>
                          <td>{formatAmount(payment.totalAmount)}</td>
                          <td>{payment.paymentMethod || "-"}</td>
                          <td>
                            <span
                              className={`label ${
                                payment.status === "Approved"
                                  ? "label-success"
                                  : payment.status === "Rejected"
                                    ? "label-danger"
                                    : "label-warning"
                              }`}
                            >
                              {payment.status || "Pending"}
                            </span>
                            {payment.status === "Rejected" && payment.rejectionReason && (
                              <>
                                <br />
                                <small className="text-danger">{payment.rejectionReason}</small>
                              </>
                            )}
                          </td>
                          <td>
                            {payment.hasReceipt ? (
                              <button
                                type="button"
                                className="btn btn-default btn-xs"
                                onClick={() => openReceipt(payment.id)}
                              >
                                <i className="fa fa-file-text-o"></i> View
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {payment.status === "Pending" ? (
                              canReviewPayments ? (
                                <div className="btn-group btn-group-xs">
                                  <button
                                    type="button"
                                    className="btn btn-success"
                                    disabled={actionId === payment.id}
                                    onClick={() => handleApprove(payment.id)}
                                  >
                                    {actionId === payment.id ? "Working..." : "Approve"}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    disabled={actionId === payment.id}
                                    onClick={() => {
                                      setRejectingPayment(payment);
                                      setRejectReason("");
                                    }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : (
                                <span className="text-muted">Awaiting reviewer</span>
                              )
                            ) : (
                              <span className="text-muted">Reviewed</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {rejectingPayment && (
        <div className="modal fade in" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setRejectingPayment(null);
                    setRejectReason("");
                  }}
                >
                  <span>&times;</span>
                </button>
                <h4 className="modal-title">Reject Receipt</h4>
              </div>
              <div className="modal-body">
                <p>
                  Reject receipt for <strong>{rejectingPayment.batchName}</strong> from{" "}
                  <strong>{rejectingPayment.schoolName}</strong>?
                </p>
                <div className="form-group">
                  <label htmlFor="rejectReason">Reason (optional)</label>
                  <textarea
                    id="rejectReason"
                    className="form-control"
                    rows={3}
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                    placeholder="Explain why this receipt was rejected"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setRejectingPayment(null);
                    setRejectReason("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={actionId === rejectingPayment.id}
                  onClick={handleReject}
                >
                  {actionId === rejectingPayment.id ? "Rejecting..." : "Reject Receipt"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default ListSchoolPayments;
